/**
 * CanvasArea.tsx
 * Zone de canvas principale utilisant react-konva
 * Remplace la gestion impérative de CanvasManager par une approche déclarative
 *
 * NOTE: Les styles dynamiques dans TextEditor sont nécessaires pour le positionnement
 * précis basé sur la position du texte dans le canvas, le zoom et le pan.
 */
import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Group, Layer, Rect, Stage, Transformer } from 'react-konva';
import Konva from 'konva';
import { useAppStore } from '../../../services/StateService';
import type { ShapeData } from '../../../types/ShapeTypes';
// FIX runtime : import statique (require() casse en ESM/Vite → "require is not defined").
import { applyShapeTransform } from '../../../utils/transformUtils';
import { SafeZoneIndicator, BleedIndicator } from './CanvasEnhancements';
import { useAppCoordinatorOptional } from '../../../hooks/useAppCoordinator';
import { AlignmentGuides } from '../../../AlignmentGuides';
import { EventBus } from '../../../services/EventBus';
import { useFontLoader } from '../../../hooks/useFontLoader';
import { useViewportCulling, getViewportBounds } from '../../../hooks/useViewportCulling';
// NETTOYÉ: CanvasInteractionService supprimé - doublon avec useViewport
import { FileUploadService } from '../../../services/FileUploadService';
import { logger } from '../../../utils/ProfessionalLogger';
import { ShapeRenderer } from './ShapeRenderer';
import { CanvasOverlays } from './CanvasOverlays';
import { CanvasSelection } from './CanvasSelection';
import { useCanvasSelection } from './useCanvasSelection';
import { ImageCropController } from './ImageCropController';
import { useViewport } from './useViewport';
import { AlignmentGuideFeedback } from './AlignmentGuideFeedback';
import { CanvasRuntime } from '../../../canvas/runtime/CanvasRuntime';
import { useRegisterCanvasStageRef } from '../../../hooks/useCanvasStageRef';
import { computeSelectionBounds } from '../../../utils/selectionBounds';
import { SelectionFloatingToolbar } from './SelectionFloatingToolbar';

// P2.7 : types locaux pour les attributs window et casts Konva.Transformer
interface WindowWithSnapbooth extends Window {
  __snapbooth_shiftKey?: boolean;
  __snapbooth_altKey?: boolean;
}

/**
 * Composant pour éditer le texte en ligne
 * NOTE: Les styles dynamiques sont nécessaires pour le positionnement précis
 * basé sur la position du texte dans le canvas, le zoom et le pan.
 */
const TextEditor: React.FC<{
  textNode: Konva.Text;
  stage: Konva.Stage;
  viewportPosition: { x: number; y: number };
  viewportScale: number;
  onFinish: (newText: string) => void;
  onCancel: () => void;
}> = ({ textNode, stage, viewportPosition, viewportScale, onFinish, onCancel }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [dynamicStyles, setDynamicStyles] = useState<React.CSSProperties>({});

  // Calcule et applique la position du textarea de façon SYNCHRONE.
  // (L'ancienne version passait par requestAnimationFrame ; le RAF était parfois
  //  annulé par un re-render avant de s'exécuter → styles jamais appliqués, la
  //  textarea restait à 0,0.)
  const updatePosition = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !textNode) return;

    // `absolutePosition()` inclut DÉJÀ l'offset du Group (50,50), le scale ET la
    // position (pan) du stage → position en pixels dans le conteneur du stage.
    const textPosition = textNode.absolutePosition();
    const stageBox = stage.container().getBoundingClientRect();
    const scale = viewportScale; // pour la taille (largeur/police), pas la position

    // Position écran = bord du conteneur + position absolue (PAS de re-scale ni
    // de re-pan : ils sont déjà dans absolutePosition).
    const absoluteX = stageBox.left + textPosition.x;
    const absoluteY = stageBox.top + textPosition.y;

    const padding = 4;
    const textWidth = textNode.width() || 200;
    const textHeight = textNode.height() || 20;
    const textAlign = textNode.align() || 'left';

    // x = bord gauche de la box du texte (Konva positionne déjà la box ici, quel
    // que soit l'alignement interne) → pas de décalage par largeur.
    setDynamicStyles({
      '--textarea-left': `${absoluteX + padding}px`,
      '--textarea-top': `${absoluteY + padding}px`,
      '--textarea-width': `${Math.max(textWidth * scale - padding * 2, 50)}px`,
      '--textarea-height': `${Math.max(textHeight * scale - padding * 2, 20)}px`,
      '--textarea-font-size': `${textNode.fontSize() * scale}px`,
      '--textarea-font-family': textNode.fontFamily(),
      '--textarea-color': textNode.fill() as string,
      '--textarea-line-height': `${textNode.lineHeight() || 1.2}`,
      '--textarea-text-align': textAlign,
      '--textarea-padding': `${padding}px`,
    } as React.CSSProperties);
  }, [textNode, stage, viewportScale]);

  useEffect(() => {
    if (!textareaRef.current || !textNode) return;

    const textarea = textareaRef.current;

    // Mettre à jour la position initiale
    updatePosition();
    textarea.focus();
    textarea.select();

    // Écouter les changements de redimensionnement de la fenêtre (throttlé)
    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });

    // Écouter les changements de zoom/pan du stage via ResizeObserver (déjà throttlé)
    const stageContainer = stage.container();
    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });
    resizeObserver.observe(stageContainer);

    // NETTOYÉ: Ne plus écouter wheel/dragmove ici car useViewport gère déjà ces événements
    // Le TextEditor sera mis à jour via les callbacks React-Konva (onWheel, onMouseMove)
    // On utilise seulement ResizeObserver pour les changements de taille

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, { capture: true });
      resizeObserver.disconnect();
    };
  }, [textNode, stage, updatePosition]);

  // Repositionner la textarea si le canvas est déplacé (pan) ou zoomé en cours d'édition.
  useEffect(() => {
    updatePosition();
  }, [viewportPosition, viewportScale, updatePosition]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      onFinish(textareaRef.current?.value || '');
    }
  };

  const handleBlur = () => {
    onFinish(textareaRef.current?.value || '');
  };

  return (
    <>
      <label htmlFor="canvas-textarea-edit" className="sr-only">
        Édition du texte sur le canvas
      </label>
      {/* Les styles dynamiques sont nécessaires pour positionner le textarea précisément
          en fonction de la position du texte dans le canvas, du zoom et du pan.
          Ces valeurs changent en temps réel et ne peuvent pas être définies en CSS statique. */}
      <textarea
        id="canvas-textarea-edit"
        ref={textareaRef}
        defaultValue={textNode.text()}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="canvas-textarea-overlay"
        style={dynamicStyles}
        aria-label="Édition du texte sur le canvas"
        placeholder="Édition du texte"
        title="Édition du texte"
        dir="auto"
      />
    </>
  );
};

/**
 * Composant principal du Canvas
 */
export function CanvasArea(): React.ReactElement {
  const appCoordinator = useAppCoordinatorOptional();
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const layerRef = useRef<Konva.Layer>(null); // Ref pour le layer principal
  const alignmentGuidesRef = useRef<AlignmentGuides | null>(null); // PHASE 4.4: Référence pour AlignmentGuides
  const [isMainLayerReady, setIsMainLayerReady] = useState(false);
  // PR-08: RAF guard pour éviter spam store lors des transforms
  const transformRafRef = useRef<number | null>(null);

  // State from Zustand store (déclarer AVANT containerSize pour éviter les erreurs)
  const shapes = useAppStore(state => state.shapes);
  // NETTOYÉ: Utiliser selection.selectedIds et selection.primaryId au lieu de canvas.selectedNodeId/selectedNodeIds
  const selectedNodeIds = useAppStore(state => state.selection.selectedIds);
  const selectedNodeId = useAppStore(state => state.selection.primaryId); // ID principal (dernier cliqué)
  const hoveredNodeId = useAppStore(state => state.canvas.hoveredNodeId); // PHASE 3.1: Pour le feedback hover
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const backgroundColor = useAppStore(state => state.canvas.backgroundColor);
  const zoomLevel = useAppStore(state => state.canvas.zoomLevel);

  // EXEMPLE: Créer une instance de CanvasRuntime pour les opérations sur le canvas
  const canvasRuntimeRef = useRef<CanvasRuntime | null>(null);

  // Enregistrer le stageRef globalement pour l'export
  useRegisterCanvasStageRef(stageRef);

  // Initialiser CanvasRuntime quand le stage est prêt
  useEffect(() => {
    if (stageRef.current && !canvasRuntimeRef.current) {
      canvasRuntimeRef.current = CanvasRuntime.create({
        stageRef,
        templateWidth,
        templateHeight,
      });
      logger.info('✅ CanvasRuntime initialisé');
    }
  }, [templateWidth, templateHeight, isMainLayerReady]);

  // EXEMPLE: Fonction pour exporter le canvas (peut être utilisée par ExportModal)
  const _handleExportCanvas = useCallback(async (pixelRatio: number = 2) => {
    if (!canvasRuntimeRef.current) {
      logger.warn("CanvasRuntime non disponible pour l'export");
      return null;
    }

    // Exemple 1: Export en DataURL
    const dataURL = canvasRuntimeRef.current.exportAsDataURL(pixelRatio);
    if (dataURL) {
      logger.info('✅ Canvas exporté en DataURL');
      // Utiliser dataURL (ex: télécharger, afficher dans une preview, etc.)
      return dataURL;
    }

    // Exemple 2: Export en Blob (alternative)
    // const blob = await canvasRuntimeRef.current.exportAsBlob(pixelRatio);
    // if (blob) {
    //   logger.info('✅ Canvas exporté en Blob');
    //   return blob;
    // }

    return null;
  }, []);

  // EXEMPLE: Fonction pour ajuster le zoom (fit to screen)
  const _handleFitToScreen = useCallback(() => {
    if (!canvasRuntimeRef.current) {
      logger.warn('CanvasRuntime non disponible pour fitToScreen');
      return;
    }
    canvasRuntimeRef.current.fitToScreen(50); // 50px de padding
  }, []);

  // EXEMPLE: Fonction pour centrer la vue sur un nœud sélectionné
  const _handleCenterOnSelected = useCallback(() => {
    if (!canvasRuntimeRef.current || !selectedNodeId) {
      return;
    }
    canvasRuntimeRef.current.centerOnNode(selectedNodeId);
  }, [selectedNodeId]);

  // EXEMPLE: Fonction pour hit test (peut être utilisée pour la sélection)
  const _handleHitTest = useCallback((pointer: { x: number; y: number }) => {
    if (!canvasRuntimeRef.current) {
      return null;
    }
    return canvasRuntimeRef.current.hitTest(pointer);
  }, []);

  // CORRIGÉ: Charger automatiquement les polices utilisées
  useFontLoader();

  // État local pour l'édition de texte
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingTextNode, setEditingTextNode] = useState<Konva.Text | null>(null);

  // PR-02: Utiliser selectShape et clearSelection au lieu de setSelectedNode
  const selectShape = useAppStore(state => state.selectShape);
  const selectShapes = useAppStore(state => state.selectShapes); // Pour la sélection multiple
  const clearSelection = useAppStore(state => state.clearSelection); // Pour désélectionner
  const setHoveredNode = useAppStore(state => state.setHoveredNode); // PHASE 3.1: Pour le feedback hover
  const updateShape = useAppStore(state => state.updateShape);
  const _applyTransform = useAppStore(state => state.applyTransform); // Action pour appliquer les transformations
  const _setZoomLevel = useAppStore(state => state.setZoomLevel);
  const alignmentGuidesEnabled = useAppStore(state => state.canvas.alignmentGuidesEnabled); // PHASE 4.4: État des guides

  // Utiliser le hook pour la sélection (React-first: utilise uniquement le store Zustand)
  const {
    selectionRect,
    handleMouseDown: handleSelectionMouseDown,
    handleMouseMove: handleSelectionMouseMove,
    handleMouseUp: handleSelectionMouseUp,
  } = useCanvasSelection({
    shapes,
    selectedNodeIds, // Source de vérité unique depuis Zustand
    selectShapes, // Action Zustand
    clearSelection, // Action Zustand
  });

  // AMÉLIORATION: Viewport culling pour optimiser les performances avec beaucoup d'objets
  const [viewportBounds, setViewportBounds] = useState<ReturnType<typeof getViewportBounds>>(null);
  // PR-17: Filtrer les shapes hidden (pas rendues)
  const visibleShapesRaw = useViewportCulling(shapes, viewportBounds, 200);
  const visibleShapes = visibleShapesRaw.filter(s => !s.hidden);

  // Mettre à jour le viewport quand le stage change
  // NETTOYÉ: Utiliser les props React-Konva (onWheel, onMouseMove) au lieu de stage.on()
  // pour éviter les doublons avec useViewport
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const updateViewport = () => {
      // SÉCURISÉ: Vérifier que stageRef.current existe avant d'appeler getViewportBounds
      const currentStage = stageRef.current;
      if (!currentStage) return;
      const bounds = getViewportBounds(currentStage);
      setViewportBounds(bounds);
    };

    // Mettre à jour au montage
    updateViewport();

    // NETTOYÉ: Ne plus écouter wheel/dragmove ici car useViewport gère déjà ces événements
    // Le viewport sera mis à jour via les callbacks de useViewport (handleWheel, handlePanMove)
    // On met à jour seulement lors des changements de zoomLevel ou nombre de shapes
  }, [zoomLevel, shapes.length]);

  // NETTOYÉ: useViewport gère ResizeObserver et dimensions du container
  const containerRef = useRef<HTMLDivElement>(null);

  // NETTOYÉ: useViewport standardisé gère tout (ResizeObserver, zoom centré, pan)
  // DOIT être appelé avant les useEffect/useCallback qui utilisent containerSize
  const { containerSize, isPanning, handleWheel, handlePanStart, handlePanMove, handlePanEnd } =
    useViewport({
      stageRef,
      containerRef,
      templateWidth,
      templateHeight,
    });

  // SEMAINE 1.1: État pour le drag & drop global
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const addShape = useAppStore(state => state.addShape);

  // AMÉLIORATION: Centrer le canvas au démarrage
  useEffect(() => {
    const stage = stageRef.current;
    const container = containerRef.current;
    if (!stage || !container) return;

    // Attendre que le stage soit complètement initialisé
    const timeoutId = setTimeout(() => {
      // SÉCURISÉ: Vérifier que stage et container existent toujours dans le timeout
      const currentStage = stageRef.current;
      const currentContainer = containerRef.current;
      if (!currentStage || !currentContainer) return;

      const rect = currentContainer.getBoundingClientRect();
      const containerWidth = rect.width || window.innerWidth;
      const containerHeight = rect.height || window.innerHeight;

      // Calculer la position pour centrer le canvas
      const scaledWidth = templateWidth * zoomLevel;
      const scaledHeight = templateHeight * zoomLevel;
      const offsetX = 50 * zoomLevel;
      const offsetY = 50 * zoomLevel;

      const centeredPos = {
        x: (containerWidth - scaledWidth) / 2 - offsetX,
        y: (containerHeight - scaledHeight) / 2 - offsetY,
      };

      // NETTOYÉ: Mettre à jour le store (useViewport synchronisera automatiquement)
      useAppStore.getState().setViewportPosition(centeredPos);
      currentStage.batchDraw();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [templateWidth, templateHeight, zoomLevel, containerSize]);

  // NETTOYÉ: Calcul de zoom automatique (fit to screen) au chargement du projet
  // Utilise le store (source unique de vérité) - useViewport synchronisera automatiquement
  const fitToScreen = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || containerSize.width === 0 || containerSize.height === 0) return;

    const projectWidth = useAppStore.getState().project.pixelWidth;
    const projectHeight = useAppStore.getState().project.pixelHeight;

    if (projectWidth === 0 || projectHeight === 0) return;

    const scaleX = containerSize.width / projectWidth;
    const scaleY = containerSize.height / projectHeight;
    const newScale = Math.min(scaleX, scaleY) * 0.9; // 90% pour marge

    // Centrer le canvas
    const x = (containerSize.width - projectWidth * newScale) / 2;
    const y = (containerSize.height - projectHeight * newScale) / 2;

    // NETTOYÉ: Mettre à jour le store (useViewport synchronisera automatiquement)
    const store = useAppStore.getState();
    store.setViewport({ scale: newScale, position: { x, y } });
    store.setZoomLevel(newScale);
  }, [containerSize]);

  // Appeler fitToScreen quand le projet est initialisé
  useEffect(() => {
    const project = useAppStore.getState().project;
    if (project.pixelWidth > 0 && project.pixelHeight > 0) {
      // Attendre que le stage soit prêt
      const timeoutId = setTimeout(() => {
        fitToScreen();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [fitToScreen, templateWidth, templateHeight]);

  // NETTOYÉ: CanvasInteractionService supprimé - doublon avec useViewport
  // useViewport gère déjà zoom (wheel) et pan (mousedown/mousemove)
  // useCanvasSelection gère déjà la sélection (mousedown/mousemove)
  // Plus besoin de CanvasInteractionService qui créait des listeners en double

  // SEMAINE 1.1: Drag & Drop global pour les images
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDraggingOver(true);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Ne masquer que si on quitte vraiment le conteneur (pas un enfant)
      if (e.currentTarget === container && !container.contains(e.relatedTarget as Node)) {
        setIsDraggingOver(false);
      }
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(false);

      const stage = stageRef.current;
      if (!stage) return;

      const rect = container.getBoundingClientRect();

      // Utiliser ReactFirstAppCoordinator pour gérer le drop d'éléments (format standardisé)
      const elementType = e.dataTransfer?.getData('application/snapbooth-type');
      if (elementType && appCoordinator) {
        try {
          // @ts-ignore - On sait que c'est le bon coordinator (ReactFirstAppCoordinator)
          appCoordinator.handleExternalDrop(e, stage);
          logger.info(`[CanvasArea] ✅ Drop géré par ReactFirstAppCoordinator: ${elementType}`);
          return; // Sortir après avoir traité l'élément
        } catch (error) {
          logger.error('[CanvasArea] Erreur lors du traitement du drop par coordinator:', error);
          // Continuer avec le fallback si le coordinator échoue
        }
      }

      // Fallback : si le coordinator n'est pas disponible ou n'a pas géré le drop,
      // vérifier manuellement si c'est un élément depuis ElementsDrawer
      const elementData = e.dataTransfer?.getData('application/json');
      if (elementData) {
        try {
          const parsed = JSON.parse(elementData);
          if (parsed.type === 'element' && parsed.element) {
            logger.warn('[CanvasArea] Coordinator non disponible, fallback sur logique directe');
            // Fallback : utiliser la logique directe si le coordinator n'est pas disponible
            const element = parsed.element;
            const { createDefaultShape } = await import('../../../types/ShapeTypes');

            const stagePos = stage.position();
            const stageScale = stage.scaleX();
            const canvasX = (e.clientX - rect.left - stagePos.x) / stageScale;
            const canvasY = (e.clientY - rect.top - stagePos.y) / stageScale;

            if (element.type === 'svg' || element.type === 'png' || element.type === 'jpg') {
              const shape = createDefaultShape('image', canvasX, canvasY);
              shape.src = element.url;
              shape.width = 200;
              shape.height = 200;
              addShape(shape);
            } else {
              const shapeType = element.name.toLowerCase().includes('rectangle')
                ? 'rect'
                : element.name.toLowerCase().includes('cercle')
                  ? 'circle'
                  : element.name.toLowerCase().includes('triangle')
                    ? 'triangle'
                    : element.name.toLowerCase().includes('étoile') ||
                        element.name.toLowerCase().includes('star')
                      ? 'star'
                      : element.name.toLowerCase().includes('cœur') ||
                          element.name.toLowerCase().includes('heart')
                        ? 'heart'
                        : 'rect';
              const shape = createDefaultShape(shapeType, canvasX, canvasY);
              addShape(shape);
            }
            return;
          }
        } catch (parseError) {
          logger.warn('[CanvasArea] Erreur lors du parsing des données de drop:', parseError);
          // Continuer avec le traitement des fichiers si le parsing échoue
        }
      }

      // Traitement des fichiers images (comportement existant)
      const files = Array.from(e.dataTransfer?.files || []);
      const imageFiles = files.filter(file => file.type.startsWith('image/'));

      if (imageFiles.length === 0) return;

      try {
        const { AssetsLibrary } = await import('../../../AssetsLibrary');
        const assetsLibrary = new AssetsLibrary();
        const { createDefaultShape } = await import('../../../types/ShapeTypes');

        // Calculer la position de dépôt sur le canvas
        const stage = stageRef.current;
        if (!stage) return;

        const rect = container.getBoundingClientRect();
        const stagePos = stage.position();
        const stageScale = stage.scaleX();

        // Convertir les coordonnées de la souris en coordonnées du canvas
        const canvasX = (e.clientX - rect.left - stagePos.x) / stageScale - 50; // -50 pour l'offset du canvas
        const canvasY = (e.clientY - rect.top - stagePos.y) / stageScale - 50;

        // Traiter chaque image
        for (let i = 0; i < imageFiles.length; i++) {
          const file = imageFiles[i];

          // Convertir en DataURL
          const dataURL = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result;
              if (typeof result === 'string') {
                resolve(result);
              } else {
                reject(new Error('Impossible de lire le fichier comme DataURL'));
              }
            };
            reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
            reader.readAsDataURL(file);
          });

          // Sauvegarder dans AssetsLibrary
          await assetsLibrary.addAsset(file.name, dataURL, 'image', []);

          // Créer l'image pour obtenir ses dimensions
          const img = new window.Image();
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("Erreur lors du chargement de l'image"));
            img.src = dataURL;
          });

          // Créer une shape image et l'ajouter au canvas
          // Décaler légèrement chaque image pour éviter le chevauchement
          const offsetX = i * 20;
          const offsetY = i * 20;
          const shape = createDefaultShape('image', canvasX + offsetX, canvasY + offsetY);
          shape.src = dataURL;
          shape.width = img.width;
          shape.height = img.height;

          addShape(shape);
        }
      } catch (error) {
        // #region agent log
        import('../../../utils/debugLogger')
          .then(({ debugLog }) => {
            debugLog({
              location: 'CanvasArea.tsx:994',
              message: 'Error in image processing',
              data: {
                errorMessage: error instanceof Error ? error.message : String(error),
                errorStack: error instanceof Error ? error.stack : undefined,
              },
              sessionId: 'debug-session',
              runId: 'run1',
              hypothesisId: 'B',
            });
          })
          .catch(() => {});
        // #endregion
        logger.error('Erreur lors du traitement des images:', { error });

        // CORRIGÉ: Notifier l'utilisateur de l'erreur
        try {
          const { ErrorNotificationService } = await import(
            '../../../services/ErrorNotificationService'
          );
          ErrorNotificationService.getInstance().notifyError(
            error instanceof Error ? error : new Error(String(error)),
            {
              severity: 'medium',
              category: 'user',
              context: { action: 'image-processing', location: 'CanvasArea' },
            }
          );
        } catch (notifyError) {
          // Fallback: utiliser useToast si disponible
          logger.warn("Impossible de notifier l'erreur via ErrorNotificationService:", {
            error: notifyError,
          });
        }
      }
    };

    container.addEventListener('dragenter', handleDragEnter);
    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('dragleave', handleDragLeave);
    container.addEventListener('drop', handleDrop);

    return () => {
      container.removeEventListener('dragenter', handleDragEnter);
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('dragleave', handleDragLeave);
      container.removeEventListener('drop', handleDrop);
    };
  }, [addShape]);

  // Handler pour le double-clic sur le texte
  // PHASE 2.1: Utilise TextToolService pour l'édition in-place
  const handleTextDoubleClick = useCallback(
    (shapeId: string, e: Konva.KonvaEventObject<MouseEvent>) => {
      const textNode = e.target as Konva.Text;

      // PHASE 2.1: Utiliser TextToolService pour l'édition in-place
      if (appCoordinator) {
        const textToolService = appCoordinator.getTextToolService();
        if (textToolService) {
          textToolService.startEditing(textNode);
          return;
        }
      }

      // Fallback: système d'édition local si TextToolService n'est pas disponible
      setEditingTextId(shapeId);
      setEditingTextNode(textNode);
      textNode.hide();
      textNode.getLayer()?.batchDraw();
    },
    []
  );

  // Handler pour le double-clic sur les espaces photo
  // CORRIGÉ: Persiste les images dans AssetsLibrary pour éviter la perte lors du démontage
  const handlePhotoSpaceDoubleClick = useCallback(
    (shapeId: string) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async e => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            // Convertir le fichier en DataURL pour la persistance
            const { AssetsLibrary } = await import('../../../AssetsLibrary');
            const assetsLibrary = new AssetsLibrary();

            const dataURL = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result;
                if (typeof result === 'string') {
                  resolve(result);
                } else {
                  reject(new Error('Impossible de lire le fichier comme DataURL'));
                }
              };
              reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
              reader.readAsDataURL(file);
            });

            // Persister dans AssetsLibrary
            await assetsLibrary.addAsset(file.name, dataURL, 'image', []);

            const img = new window.Image();
            img.onload = () => {
              // Récupérer l'ancienne src pour la nettoyer si c'est une Blob URL
              const oldShape = shapes.find(s => s.id === shapeId);
              if (oldShape?.src && oldShape.src.startsWith('blob:')) {
                const fileUploadService = FileUploadService.getInstance();
                fileUploadService.revokeBlobURL(oldShape.src);
              }

              // Utiliser le DataURL persisté au lieu d'une Blob URL volatile
              updateShape(shapeId, {
                src: dataURL,
                width: img.width,
                height: img.height,
              });
            };
            img.src = dataURL;
          } catch (error) {
            logger.error("Erreur lors du chargement de l'image:", { error });
          }
        }
      };
      input.click();
    },
    [updateShape, shapes]
  );

  // PHASE 4.4: Initialiser AlignmentGuides après la création du stage
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const mainLayer = layerRef.current || (stage.findOne('#main-layer') as Konva.Layer | null);

    if (!mainLayer) return;

    // Initialiser AlignmentGuides
    if (!alignmentGuidesRef.current) {
      alignmentGuidesRef.current = new AlignmentGuides(stage, mainLayer);
    }

    // Synchroniser l'état activé/désactivé
    if (alignmentGuidesRef.current) {
      alignmentGuidesRef.current.setEnabled(alignmentGuidesEnabled);
    }

    // Cleanup
    return () => {
      if (alignmentGuidesRef.current) {
        alignmentGuidesRef.current.destroy();
        alignmentGuidesRef.current = null;
      }
    };
  }, [alignmentGuidesEnabled, isMainLayerReady, shapes.length]); // Réinitialiser si le nombre de shapes change (layer peut être recréé)

  // Assurer que layerRef est toujours défini après montage
  useEffect(() => {
    const ensureMainLayerRef = () => {
      // SÉCURISÉ: Logique corrigée - vérifier que layerRef n'existe PAS et que stageRef existe
      if (layerRef.current || !stageRef.current) return;
      const stage = stageRef.current;
      const mainLayer = stage.findOne('#main-layer') as Konva.Layer | null;
      if (mainLayer) {
        layerRef.current = mainLayer;
        setIsMainLayerReady(true);
      }
    };

    ensureMainLayerRef();
    const timeoutId = setTimeout(ensureMainLayerRef, 0);

    return () => clearTimeout(timeoutId);
  }, []);

  // PHASE 4.4: Synchroniser l'état des guides avec le store
  useEffect(() => {
    if (alignmentGuidesRef.current) {
      alignmentGuidesRef.current.setEnabled(alignmentGuidesEnabled);
    }
  }, [alignmentGuidesEnabled]);

  // SEMAINE 2.2: Gérer les touches modifirices pour le Transformer (Shift+Resize, Alt+Resize)
  // NETTOYÉ: Vérifier les inputs/textarea avant de traiter Shift/Alt
  useEffect(() => {
    interface WindowWithSnapbooth extends Window {
      __snapbooth_shiftKey?: boolean;
      __snapbooth_altKey?: boolean;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorer si on est dans un champ de saisie
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // UNDO/REDO: Ctrl+Z / Ctrl+Y (ou Ctrl+Shift+Z)
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' || e.key === 'Z') {
          e.preventDefault();
          const store = useAppStore.getState();
          if (e.shiftKey) {
            // Ctrl+Shift+Z = Redo
            if (store.canRedo()) {
              store.redo();
              logger.info('↷ Redo via Ctrl+Shift+Z');
            }
          } else {
            // Ctrl+Z = Undo
            if (store.canUndo()) {
              store.undo();
              logger.info('↶ Undo via Ctrl+Z');
            }
          }
          return;
        } else if (e.key === 'y' || e.key === 'Y') {
          // Ctrl+Y = Redo
          e.preventDefault();
          const store = useAppStore.getState();
          if (store.canRedo()) {
            store.redo();
            logger.info('↷ Redo via Ctrl+Y');
          }
          return;
        }
      }

      // Gérer Shift/Alt pour le Transformer
      if (e.shiftKey) {
        (window as WindowWithSnapbooth).__snapbooth_shiftKey = true;
      }
      if (e.altKey) {
        (window as WindowWithSnapbooth).__snapbooth_altKey = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Ignorer si on est dans un champ de saisie
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (!e.shiftKey) {
        (window as WindowWithSnapbooth).__snapbooth_shiftKey = false;
      }
      if (!e.altKey) {
        (window as WindowWithSnapbooth).__snapbooth_altKey = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // CORRIGÉ: Synchroniser le Transformer avec selectedNodeIds (source de vérité unique depuis Zustand)
  // RÈGLE: selectedNodeIds vient UNIQUEMENT du store Zustand
  // Quand selectedNodeIds change, on trouve les nodes et on les passe au Transformer
  // PR-17: Filtrer les shapes locked/hidden du Transformer
  const selectedIdsForTransform = selectedNodeIds.filter(id => {
    const s = shapes.find(x => x.id === id);
    return s && !s.hidden && !s.locked;
  });

  useEffect(() => {
    const transformer = transformerRef.current;
    const stage = stageRef.current;
    if (!transformer || !stage) return;

    // PR-17: Utiliser selectedIdsForTransform (filtre locked/hidden)
    if (selectedIdsForTransform.length > 0) {
      // Trouver tous les nœuds sélectionnés dans le stage
      const nodes = selectedIdsForTransform
        .map(id => stage.findOne(`#${id}`))
        .filter((node): node is Konva.Node => node !== null && node !== undefined);

      if (nodes.length > 0) {
        transformer.nodes(nodes);
        transformer.getLayer()?.batchDraw();
      } else {
        transformer.nodes([]);
      }
    } else {
      transformer.nodes([]);
    }
  }, [selectedIdsForTransform, shapes]); // PR-17: Dépendance sur selectedIdsForTransform

  // CORRIGÉ: Handler pour terminer l'édition de texte avec synchronisation robuste
  const handleTextEditFinish = useCallback(
    (newText: string) => {
      if (editingTextId) {
        // Mettre à jour le store avec le nouveau texte
        updateShape(editingTextId, { text: newText.trim() || 'Texte' });

        // Si on a une référence au node Konva, mettre à jour aussi
        if (editingTextNode) {
          editingTextNode.text(newText.trim() || 'Texte');
          // Recalculer la taille si nécessaire
          // Konva.Text accepte 'auto' pour height mais TypeScript ne le reconnaît pas
          editingTextNode.height('auto' as unknown as number);
          editingTextNode.show();
          editingTextNode.getLayer()?.batchDraw();
        }
      }
      setEditingTextId(null);
      setEditingTextNode(null);
    },
    [editingTextId, editingTextNode, updateShape]
  );

  // Handler pour annuler l'édition de texte
  const handleTextEditCancel = useCallback(() => {
    if (editingTextNode) {
      editingTextNode.show();
      editingTextNode.getLayer()?.batchDraw();
    }
    setEditingTextId(null);
    setEditingTextNode(null);
  }, [editingTextNode]);

  // Note: La synchronisation du transformer est déjà gérée dans l'useEffect précédent (ligne 565)
  // qui gère maintenant la sélection multiple avec selectedNodeIds

  // PHASE 2.1: Écouter l'événement 'text-edit-start' pour déclencher l'édition automatique
  useEffect(() => {
    const eventBus = EventBus.getInstance();

    const handleTextEditStart = (event: {
      type: string;
      data?: { shapeId: string; textNode: Konva.Text };
    }) => {
      if (event.data && event.data.shapeId && event.data.textNode) {
        const { shapeId, textNode } = event.data;
        // Déclencher l'édition en définissant l'état local
        setEditingTextId(shapeId);
        setEditingTextNode(textNode);
        textNode.hide();
        textNode.getLayer()?.batchDraw();
      }
    };

    eventBus.on('text-edit-start', handleTextEditStart);

    return () => {
      eventBus.off('text-edit-start', handleTextEditStart);
    };
  }, []);

  // FIABILISATION : déclencheur clavier pour l'édition de texte (Entrée / F2),
  // indépendant du double-clic Konva (capricieux). Convention Canva/PowerPoint.
  useEffect(() => {
    const handleEditKey = (e: KeyboardEvent) => {
      if (editingTextId) return; // déjà en édition
      if (e.key !== 'Enter' && e.key !== 'F2') return;

      // Ne pas interférer si l'utilisateur tape dans un champ de saisie.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;

      if (!selectedNodeId) return;
      const shape = shapes.find(s => s.id === selectedNodeId);
      if (!shape || shape.type !== 'text') return;

      const node = stageRef.current?.findOne(`#${selectedNodeId}`) as Konva.Text | undefined;
      if (!node) return;

      e.preventDefault();
      setEditingTextId(selectedNodeId);
      setEditingTextNode(node);
      node.hide();
      node.getLayer()?.batchDraw();
    };

    window.addEventListener('keydown', handleEditKey);
    return () => window.removeEventListener('keydown', handleEditKey);
  }, [editingTextId, selectedNodeId, shapes]);

  // Handle stage click for deselection
  // PR-02: Utiliser clearSelection() au lieu de setSelectedNode(null)
  const handleStageClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      // Click on empty area - deselect
      if (e.target === e.target.getStage()) {
        clearSelection();
      }
    },
    [clearSelection]
  );

  // NETTOYÉ: Viewport depuis Zustand (source unique de vérité)
  const viewportPosition = useAppStore(state => state.canvas.viewport.position);
  // PR-04: Définir viewportScale pour TextEditor
  const viewportScale = useAppStore(state => state.canvas.viewport.scale);

  // PHASE 3: Calculer les dimensions du stage avec zoom et responsive
  // Utiliser les dimensions du conteneur détectées par ResizeObserver
  // S'assurer que le canvas prend au moins la taille du conteneur
  const containerWidth = containerSize.width > 0 ? containerSize.width : window.innerWidth;
  const containerHeight = containerSize.height > 0 ? containerSize.height : window.innerHeight;

  // PR-09: Calculer les bounds de sélection multi-select
  const selectionIds =
    selectedNodeIds.length > 0 ? selectedNodeIds : selectedNodeId ? [selectedNodeId] : [];

  const selectionBounds = computeSelectionBounds(shapes, selectionIds);

  const renderShapes = useCallback(() => {
    const alignmentGuides = alignmentGuidesRef.current;

    const renderShape = (shape: ShapeData) => {
      // P1.6 FIX: Handler pour les transformations (position + size + rotation)
      // Utiliser transformUtils pour centraliser les transformations
      const handleTransform = (attrs: Partial<ShapeData>) => {
        // Si c'est une transformation (x, y, width, height, rotation), utiliser transformUtils
        if (
          'x' in attrs ||
          'y' in attrs ||
          'width' in attrs ||
          'height' in attrs ||
          'rotation' in attrs
        ) {
          applyShapeTransform(shape.id, {
            x: attrs.x ?? shape.x ?? 0,
            y: attrs.y ?? shape.y ?? 0,
            width: attrs.width ?? shape.width ?? 100,
            height: attrs.height ?? shape.height ?? 100,
            rotation: attrs.rotation ?? shape.rotation ?? 0,
          });
        } else {
          // Pour les autres propriétés (fill, text, etc.), utiliser updateShape
          updateShape(shape.id, attrs);
        }
      };

      return (
        <ShapeRenderer
          key={shape.id}
          shape={shape}
          isSelected={selectedNodeIds.includes(shape.id)}
          isHovered={hoveredNodeId === shape.id}
          onSelect={() => selectShape(shape.id)}
          onChange={handleTransform}
          onDoubleClick={e => {
            if (shape.type === 'text') {
              handleTextDoubleClick(shape.id, e);
            } else if (shape.type === 'photoSpace') {
              handlePhotoSpaceDoubleClick(shape.id);
            }
          }}
          onMouseEnter={() => setHoveredNode(shape.id)}
          onMouseLeave={() => setHoveredNode(null)}
          alignmentGuides={alignmentGuides}
        />
      );
    };

    const backgroundShapes = visibleShapes.filter(shape => {
      if (shape.type === 'photoSpace') return false;
      if (shape.type === 'text' && shape.layer !== 'background') return false;
      if (shape.layer === 'background') return true;
      if (shape.layer === 'photos' || shape.layer === 'overlay') return false;
      const zIndex = shape.zIndex ?? 0;
      return zIndex < 10;
    });

    const photoShapes = visibleShapes.filter(shape => {
      if (shape.layer === 'photos') return true;
      if (shape.layer === 'background' || shape.layer === 'overlay') return false;
      if (shape.type === 'photoSpace') return true;
      const zIndex = shape.zIndex ?? 0;
      return zIndex >= 10 && zIndex < 20 && shape.type !== 'text';
    });

    const overlayShapes = visibleShapes.filter(shape => {
      if (shape.layer === 'overlay') return true;
      if (shape.layer === 'background' || shape.layer === 'photos') return false;
      if (shape.type === 'photoSpace') return false;
      if (shape.type === 'text') return true;
      if (shape.type === 'image' || shape.type === 'rect') {
        const zIndex = shape.zIndex ?? 0;
        return zIndex >= 10;
      }
      return true;
    });

    return (
      <>
        <Group name="backgroundLayer" className="backgroundLayer" x={50} y={50}>
          <Rect
            name="template-bg"
            className="template-bg"
            x={0}
            y={0}
            width={templateWidth}
            height={templateHeight}
            fill={backgroundColor}
            listening={false}
          />
          <BleedIndicator />
          <SafeZoneIndicator />
          {backgroundShapes.map(renderShape)}
        </Group>

        <Group name="photosLayer" className="photosLayer" x={50} y={50}>
          {photoShapes.map(renderShape)}
        </Group>

        <Group name="overlayLayer" className="overlayLayer" x={50} y={50}>
          {overlayShapes.map(renderShape)}
        </Group>
      </>
    );
  }, [
    backgroundColor,
    handlePhotoSpaceDoubleClick,
    handleTextDoubleClick,
    hoveredNodeId,
    selectedNodeId,
    selectedNodeIds,
    setHoveredNode,
    selectShape,
    templateHeight,
    templateWidth,
    updateShape,
    visibleShapes,
  ]);

  return (
    <div
      className="snapbooth-canvas-area"
      ref={containerRef}
      style={{
        position: 'relative',
        ...(isDraggingOver && {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '2px dashed #3b82f6',
          borderRadius: '8px',
        }),
      }}
    >
      {/* SEMAINE 1.1: Indicateur visuel lors du drag & drop */}
      {isDraggingOver && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            backgroundColor: 'rgba(59, 130, 246, 0.9)',
            color: 'white',
            padding: '24px 48px',
            borderRadius: '12px',
            fontSize: '18px',
            fontWeight: '600',
            pointerEvents: 'none',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          }}
        >
          📷 Déposez les images ici
        </div>
      )}

      {/* Overlays (Rulers, NavigationBar, Minimap, etc.) */}
      <CanvasOverlays
        templateWidth={templateWidth}
        templateHeight={templateHeight}
        zoomLevel={zoomLevel}
        stageRef={stageRef}
        containerRef={containerRef}
        stagePosition={viewportPosition}
        onStagePositionChange={pos => {
          // NETTOYÉ: Mettre à jour le store (useViewport synchronisera automatiquement)
          useAppStore.getState().setViewportPosition(pos);
        }}
        selectionIds={selectionIds}
        selectionBounds={selectionBounds}
      />

      {/* Description pour les lecteurs d'écran */}
      <div id="canvas-description" className="sr-only">
        Zone de dessin principale pour créer et éditer des templates de photobooth. Utilisez les
        outils de la barre latérale pour ajouter des formes, du texte et des images. Utilisez la
        molette de la souris pour zoomer, et maintenez le clic droit pour déplacer le canvas.
      </div>

      <Stage
        ref={stageRef}
        width={containerWidth}
        height={containerHeight}
        onClick={handleStageClick}
        onTap={handleStageClick}
        onWheel={handleWheel}
        onContextMenu={e => e.evt.preventDefault()} // Désactiver le menu contextuel pour le panning
        role="application"
        aria-label="Zone de dessin - Éditeur de templates photobooth"
        aria-describedby="canvas-description"
        aria-live="polite"
        tabIndex={0}
        // AMÉLIORATION: Panning avec clic droit/milieu et touch + sélection
        onMouseDown={e => {
          const shouldPan = handlePanStart(e);
          // Ne pas déclencher la sélection si on panne
          if (!shouldPan) {
            handleSelectionMouseDown(e);
          }
        }}
        onTouchStart={handlePanStart}
        onMouseMove={e => {
          handlePanMove(e);
          if (!isPanning) {
            handleSelectionMouseMove(e);
          }
        }}
        onTouchMove={handlePanMove}
        onMouseUp={e => {
          handlePanEnd();
          if (!isPanning) {
            handleSelectionMouseUp(e);
          }
        }}
        onTouchEnd={handlePanEnd}
        scaleX={zoomLevel}
        scaleY={zoomLevel}
        x={viewportPosition.x}
        y={viewportPosition.y}
      >
        <Layer
          ref={node => {
            if (node) {
              layerRef.current = node;
              setIsMainLayerReady(true);
            }
          }}
          id="main-layer"
        >
          <Rect
            x={50}
            y={50}
            width={templateWidth}
            height={templateHeight}
            fill={backgroundColor}
            shadowColor="rgba(0,0,0,0.1)"
            shadowBlur={20}
            shadowOffset={{ x: 0, y: 4 }}
            name="background-rect"
            listening={false}
          />
          {renderShapes()}
        </Layer>

        <Layer id="ui-layer">
          {alignmentGuidesRef.current && layerRef.current && stageRef.current && (
            <AlignmentGuideFeedback
              stage={stageRef.current}
              layer={layerRef.current}
              alignmentGuides={
                alignmentGuidesRef.current as unknown as {
                  [key: string]: unknown;
                  points: () => number[];
                }[]
              }
            />
          )}

          {/* Rectangle de sélection (converti écran → contenu du stage) */}
          <CanvasSelection
            selectionRect={selectionRect}
            stagePos={viewportPosition}
            stageScale={zoomLevel}
          />

          {/* Transformer pour la sélection */}
          <Transformer
            ref={transformerRef}
            boundBoxFunc={(oldBox, newBox) => {
              // Limit resize
              if (newBox.width < 5 || newBox.height < 5) {
                return oldBox;
              }
              return newBox;
            }}
            rotateEnabled={true}
            enabledAnchors={[
              'top-left',
              'top-right',
              'bottom-left',
              'bottom-right',
              'middle-left',
              'middle-right',
              'top-center',
              'bottom-center',
            ]}
            // PR-08: Transformer devient le chef d'orchestre pour multi-select
            onTransformStart={e => {
              const transformer = e.target as unknown as Konva.Transformer;
              const nodes = transformer.nodes() as Konva.Node[];
              if (nodes.length === 0) return;

              const store = useAppStore.getState();
              store.beginGesture(
                'transform',
                nodes.map(n => n.id())
              );

              // Stocker l'état initial pour CHAQUE node
              nodes.forEach(node => {
                node.setAttr('initialWidth', node.width());
                node.setAttr('initialHeight', node.height());
                node.setAttr('initialX', node.x());
                node.setAttr('initialY', node.y());
              });
            }}
            onTransform={e => {
              const transformer = e.target as unknown as Konva.Transformer;
              const nodes = transformer.nodes() as Konva.Node[];
              if (nodes.length === 0) return;

              // PR-08: Rotation snap global
              const guides = alignmentGuidesRef.current;

              // Touches globales
              const isShiftPressed = (window as WindowWithSnapbooth).__snapbooth_shiftKey || false;
              const isAltPressed = (window as WindowWithSnapbooth).__snapbooth_altKey || false;

              // PR-08: Throttle RAF pour éviter spam store
              if (transformRafRef.current) return;

              transformRafRef.current = requestAnimationFrame(() => {
                transformRafRef.current = null;

                const store = useAppStore.getState();

                // PR-08: Traiter TOUS les nodes (multi-select)
                nodes.forEach(node => {
                  const scaleX = node.scaleX();
                  const scaleY = node.scaleY();

                  const initialWidth = node.getAttr('initialWidth') || node.width();
                  const initialHeight = node.getAttr('initialHeight') || node.height();
                  const initialX = node.getAttr('initialX') || node.x();
                  const initialY = node.getAttr('initialY') || node.y();

                  // Snap rotation
                  if (guides && guides.isEnabled()) {
                    const currentRotation = node.rotation();
                    const snappedRotation = guides.checkRotationSnap(currentRotation);
                    if (snappedRotation !== currentRotation) {
                      node.rotation(snappedRotation);
                    }
                  }

                  // Shift = keep ratio
                  if (isShiftPressed) {
                    const maxScale = Math.max(Math.abs(scaleX), Math.abs(scaleY));
                    node.scaleX(scaleX >= 0 ? maxScale : -maxScale);
                    node.scaleY(scaleY >= 0 ? maxScale : -maxScale);
                  }

                  // Alt = resize from center
                  if (isAltPressed) {
                    const newWidth = initialWidth * Math.abs(scaleX);
                    const newHeight = initialHeight * Math.abs(scaleY);
                    const deltaX = (newWidth - initialWidth) / 2;
                    const deltaY = (newHeight - initialHeight) / 2;
                    node.x(initialX - deltaX);
                    node.y(initialY - deltaY);
                  }

                  // Snap position during transform
                  if (guides && guides.isEnabled()) {
                    const newWidth = Math.max(5, initialWidth * Math.abs(scaleX));
                    const newHeight = Math.max(5, initialHeight * Math.abs(scaleY));

                    const snapped = guides.checkAlignmentDuringTransform(
                      node,
                      newWidth,
                      newHeight,
                      node.x(),
                      node.y()
                    );

                    if (snapped.x !== node.x() || snapped.y !== node.y()) {
                      node.x(snapped.x);
                      node.y(snapped.y);
                    }
                  }

                  // Sync store pour CHAQUE node
                  const finalWidth = Math.max(5, initialWidth * Math.abs(node.scaleX()));
                  const finalHeight = Math.max(5, initialHeight * Math.abs(node.scaleY()));

                  store.applyTransform(node.id(), {
                    x: node.x(),
                    y: node.y(),
                    width: finalWidth,
                    height: finalHeight,
                    rotation: node.rotation(),
                  });
                });
              });
            }}
            onTransformEnd={e => {
              const transformer = e.target as unknown as Konva.Transformer;
              const nodes = transformer.nodes() as Konva.Node[];
              if (nodes.length === 0) return;

              const store = useAppStore.getState();

              // PR-08: Reset scales pour TOUS les nodes
              nodes.forEach(node => {
                const scaleX = node.scaleX();
                const scaleY = node.scaleY();

                node.scaleX(1);
                node.scaleY(1);

                store.applyTransform(node.id(), {
                  x: node.x(),
                  y: node.y(),
                  width: Math.max(5, node.width() * Math.abs(scaleX)),
                  height: Math.max(5, node.height() * Math.abs(scaleY)),
                  rotation: node.rotation(),
                });
              });

              // Clear guides
              alignmentGuidesRef.current?.clearGuides?.();

              // PR-08: 1 seul commit history via endGesture
              store.endGesture();
            }}
          />
        </Layer>
      </Stage>

      {/* Éditeur de texte en superposition */}
      {editingTextId && editingTextNode && stageRef.current && (
        <TextEditor
          textNode={editingTextNode}
          stage={stageRef.current}
          viewportPosition={viewportPosition}
          viewportScale={viewportScale}
          onFinish={handleTextEditFinish}
          onCancel={handleTextEditCancel}
        />
      )}

      {/* QuickActionsMenu désactivé : redondant avec SelectionFloatingToolbar et
          positionné en dur (400,300) → encombrait le canvas. La barre contextuelle
          ci-dessous est l'unique source d'actions sur la sélection. */}

      {/* P3 Snapbooth Redesign : Floating toolbar contextuelle au-dessus de la sélection.
          Positionnée en absolu, suit le viewport (scale + position).
          Skip si édition de texte inline en cours (TextEditor prend le relais). */}
      {selectedNodeId &&
        !editingTextId &&
        selectedNodeIds.length <= 1 &&
        (() => {
          const selectedShape = shapes.find(s => s.id === selectedNodeId);
          if (!selectedShape) return null;
          // FIX positionnement : les shapes sont rendues dans un Group offset de
          // (50,50). On AJOUTE cet offset, sinon la barre flottait en haut-à-gauche
          // de la sélection réelle (décalage de 50*scale px).
          const CANVAS_OFFSET = 50;
          const x =
            ((selectedShape.x ?? 0) + CANVAS_OFFSET) * viewportScale +
            viewportPosition.x +
            ((selectedShape.width ?? 0) * viewportScale) / 2;
          const y =
            ((selectedShape.y ?? 0) + CANVAS_OFFSET) * viewportScale + viewportPosition.y - 8;
          return <SelectionFloatingToolbar shape={selectedShape} x={x} y={y} />;
        })()}

      {/* Contrôleur de recadrage d'image (écoute snapbooth:edit-crop) */}
      <ImageCropController />
    </div>
  );
}

export default CanvasArea;
